import { LegacyPortalSearchService } from "@spwn-portal/grpc";
import { MEDIA_QUERY_CONDITION_FOR_PC } from "constants/mediaQueryForPc";
import { useI18n } from "hooks/i18n/i18n";
import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { usePublicConnectClient } from "utility/connectWeb";
import { isFeatureEnabled } from "utility/isFeatureEnabled";
import { getBelongedHosting } from "utility/storage";

import { createGetContents } from "./functions/createGetContents";
import { useEventSearch } from "./functions/useEventSearch";
import { useGetGenreConditions } from "./functions/useGetGenreConditions";
import { useGoodsSearch } from "./functions/useGoodsSearch";
import { useSearch } from "./functions/useSearch";
import { useTab } from "./functions/useTab";

import type { Props } from "./props";
import type { Breadcrumb } from "components/atoms/Breadcrumb";
import type { ComponentProps, ChangeEventHandler } from "react";
import type { Size } from "ui/Tab/types";

type Input = Props;

export const useThis = (input: Input) => {
  const { email } = input;
  const { t } = useI18n();
  const belongedHosting = getBelongedHosting();
  const client = usePublicConnectClient(LegacyPortalSearchService);
  const [keyword, setKeyword] = useState("");
  const handleChangeKeyword: ChangeEventHandler<HTMLInputElement> = (event) => {
    setKeyword(event.target.value);
  };
  const [isModalOpen, setModalOpen] = useState(false);
  const {
    eventResultCount,
    eventSearchResults,
    handleLoadMoreEvents,
    handleSearchEvent,
    hasEventSearched,
    isLoading: isEventLoading,
    isMoreLoading: isMoreEventLoading,
  } = useEventSearch({
    belongedHosting,
    client,
  });
  const {
    goodsResultCount,
    goodsSearchResults,
    handleLoadMoreGoodsList,
    handleSearchGoods,
    hasGoodsSearched,
    isLoading: isGoodsLoading,
    isMoreLoading: isMoreGoodsLoading,
  } = useGoodsSearch({
    belongedHosting,
    client,
  });
  useSearch({ handleSearchEvent, handleSearchGoods, setKeyword });
  const { genreConditions } = useGetGenreConditions({ client });
  const { handleSelectTab, search } = useTab();

  const isDetailSearchEnabled = isFeatureEnabled({
    email: email ?? "",
    featureName: "enableDetailSearch",
  });
  const hasSearched = hasEventSearched || hasGoodsSearched;
  const isPC = useMediaQuery(MEDIA_QUERY_CONDITION_FOR_PC);
  const tabSize: Size = isPC ? "large" : "medium";

  const getContents = createGetContents({
    eventResultCount,
    eventSearchResults,
    goodsResultCount,
    goodsSearchResults,
    handleLoadMoreEvents,
    handleLoadMoreGoodsList,
    hasSearched,
    isLoading: isEventLoading || isGoodsLoading,
    isMoreLoading: isMoreEventLoading || isMoreGoodsLoading,
    onSelectTab: handleSelectTab,
    search,
    t,
    tabSize,
  });

  const breadcrumbPaths: ComponentProps<typeof Breadcrumb>["paths"] = [
    ["/", "ホーム"],
    ["", t("common.routes.search")],
  ];

  return {
    breadcrumbPaths,
    eventResultCount,
    genreConditions,
    getContents,
    handleChangeKeyword,
    hasSearched,
    isDetailSearchEnabled,
    isModalOpen,
    keyword,
    setModalOpen,
  };
};
